import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { getIcon } from "@/components/iconMap";

const FOOTER_COLS = [
  {
    title: "Studio",
    links: [
      { to: "/services", label: "Services" },
      { to: "/portfolio", label: "Portfolio" },
      { to: "/team", label: "Our Team" },
      { to: "/story", label: "Founder Story" },
      { to: "/presskit", label: "Press Kit" },
      { to: "/careers", label: "Careers" },
      { to: "/join-sales", label: "Join Sales Team" },
      { to: "/join-creators", label: "Creator Program" },
      { to: "/contact", label: "Contact" },
    ],
  },
  {
    title: "Products",
    links: [
      { to: "/new-citizen", label: "New Citizen" },
      { to: "/new-citizen/workshop", label: "Mod Workshop" },
      { to: "/asset-packs", label: "Asset Packs" },
      { to: "https://store.steampowered.com/app/1084840/Living_Dead/", label: "Living Dead", external: true },
      { to: "/news", label: "News Hub" },
    ],
  },
  {
    title: "Services by Engine",
    links: [
      { to: "/unreal-engine-development", label: "Unreal Engine 5" },
      { to: "/arma-reforger-development", label: "Arma Reforger" },
      { to: "/fivem-development", label: "FiveM (GTA V)" },
      { to: "/minecraft-development", label: "Minecraft" },
      { to: "/unity-development", label: "Unity" },
      { to: "/custom-game-modding", label: "Custom / Other" },
    ],
  },
  {
    title: "Legal",
    links: [
      { to: "/eula", label: "EULA" },
      { to: "/nda", label: "Non-Disclosure" },
      { to: "/privacy", label: "Privacy Policy" },
      { to: "/refunds", label: "Refund Policy" },
    ],
  },
];

export default function SiteFooter() {
  const [roadmap, setRoadmap] = useState(null);
  useEffect(() => {
    api.get("/roadmap").then((r) => setRoadmap(r.data)).catch(() => {});
  }, []);
  const socials = (roadmap?.links || []).filter((l) => l.url);

  return (
    <footer data-testid="site-footer" className="border-t border-white/10 mt-auto bg-black/40">
      <div data-testid="footer-cta-strip" className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 flex flex-col md:flex-row md:items-center gap-6 justify-between">
          <div>
            <p className="font-sans font-bold uppercase text-xl md:text-2xl text-zinc-50">Have a project in mind?</p>
            <p className="font-mono text-[10px] tracking-[0.2em] text-zinc-500 uppercase mt-2">Fixed-price quote back within 24 hours // NDA-ready // Escrow-protected</p>
          </div>
          <Link
            to="/contact"
            data-testid="footer-quote-cta"
            className="shrink-0 bg-nc-orange hover:bg-nc-orange/80 text-white font-sans font-semibold uppercase tracking-[0.2em] text-xs px-7 py-3.5 inline-flex items-center gap-2 transition-colors duration-300"
            style={{ boxShadow: "0 0 24px rgba(255,107,26,0.25)" }}
          >
            Start Your Project <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-12 pb-8 flex flex-col gap-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-[1.5fr_1fr_1fr_1fr_1fr] gap-8">
          <div>
            <div className="flex items-center gap-4">
              <img src="/assets/specpixel-logo-transparent.png" alt="Specpixel Entertainment" className="brand-mark h-12 w-12 rounded-full" />
              <div className="h-10 w-px bg-white/15" />
              <div className="flex flex-col">
                <span className="font-sans font-bold text-base tracking-[0.2em] text-zinc-100 leading-none">SPECPIXEL</span>
                <span className="font-mono text-[9px] tracking-[0.35em] text-zinc-500 leading-none mt-1">ENTERTAINMENT</span>
              </div>
            </div>
            <p className="font-mono text-[10px] tracking-[0.15em] text-zinc-500 uppercase mt-4 leading-relaxed">
              Australian game development & outsourcing studio. Melbourne, Australia.
            </p>
            {socials.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 mt-4">
                {socials.map((l) => {
                  const Icon = getIcon(l.icon);
                  return (
                    <a
                      key={l.id}
                      data-testid={`footer-social-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      href={l.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={l.label}
                      className="w-8 h-8 border border-white/10 flex items-center justify-center text-zinc-500 hover:text-nc-orange hover:border-nc-orange/50 transition-colors duration-300"
                    >
                      <Icon className="w-3.5 h-3.5" />
                    </a>
                  );
                })}
              </div>
            )}
          </div>

          {FOOTER_COLS.map((col) => (
            <div key={col.title}>
              <p className="font-mono text-[10px] tracking-[0.3em] text-zinc-500 uppercase mb-4">{col.title}</p>
              <div className="flex flex-col gap-2.5">
                {col.links.map((l) =>
                  l.external ? (
                    <a key={l.label} href={l.to} target="_blank" rel="noopener noreferrer" className="font-mono text-[11px] tracking-[0.15em] text-zinc-400 hover:text-nc-orange uppercase transition-colors duration-300">
                      {l.label}
                    </a>
                  ) : (
                    <Link key={l.label} to={l.to} className="font-mono text-[11px] tracking-[0.15em] text-zinc-400 hover:text-nc-orange uppercase transition-colors duration-300">
                      {l.label}
                    </Link>
                  )
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="border-t border-white/10 pt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <span className="font-mono text-[10px] tracking-[0.2em] text-zinc-600 uppercase">
            © 2026 Specpixel Entertainment PTY LTD. All rights reserved.
          </span>
          <Link to="/admin/login" data-testid="admin-link" className="font-mono text-[10px] tracking-[0.25em] text-zinc-600 hover:text-nc-blue uppercase flex items-center gap-2 transition-colors duration-300">
            <Lock className="w-3 h-3" /> Admin
          </Link>
        </div>
      </div>
    </footer>
  );
}
